import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { registerSW } from 'virtual:pwa-register'
import { convexVue } from 'convex-vue'
import { clerkPlugin } from '@clerk/vue'
import * as Sentry from '@sentry/vue'

import './style.css'
import App from './App.vue'
import router from './router'
import { clerkLocalization } from '@/lib/clerkUi'
import { initPostHog } from '@/lib/posthog'
import { trackPageView } from '@/lib/sentryMetrics'
import { persistClerkEmailLinkQueryFromLocation } from '@/features/booking/lib/clerkEmailLinkQuery'

// Clerk handshake/load strips `__clerk_*` query params. Snapshot before the plugin boots.
persistClerkEmailLinkQueryFromLocation()

// Register service worker for static asset caching (noop in serve — PWA devOptions.enabled:false).
registerSW()

const app = createApp(App)

app.use(createPinia())
app.use(router)

initPostHog()

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.MODE !== 'development',
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  // Metrics enabled by default (≥10.25); product counters in lib/sentryMetrics.ts
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    // Defaults: maskAllText + blockAllMedia (PII-safe for booking UI)
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
  tracePropagationTargets: [/^https:\/\/.*\.convex\.cloud/, import.meta.env.VITE_CONVEX_URL],
  // Prod: 10% full sessions; always capture session around errors
  replaysSessionSampleRate: import.meta.env.PROD ? 1.0 : 1.0,
  replaysOnErrorSampleRate: 1.0,
})

// New deploy deletes old hashed chunks → stale tab hits vite:preloadError.
// Reload once so the browser picks up the fresh index.html + assets.
const VITE_PRELOAD_RELOAD_KEY = 'vite:preload-reload'
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  if (sessionStorage.getItem(VITE_PRELOAD_RELOAD_KEY)) {
    Sentry.captureException(
      event.payload instanceof Error ? event.payload : new Error('Vite preload failed after reload'),
      {
        tags: { 'vite.preload': 'reload-exhausted' },
      },
    )
    return
  }
  sessionStorage.setItem(VITE_PRELOAD_RELOAD_KEY, '1')
  window.location.reload()
})

app.use(convexVue, {
  url: import.meta.env.VITE_CONVEX_URL,
})

router.afterEach((to) => {
  trackPageView(String(to.name))
})

app.use(clerkPlugin, {
  publishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
  localization: clerkLocalization,
  // Without these Clerk uses the hosted Account Portal for OAuth callbacks and auth errors,
  // so a social sign-in with an unknown account would end up on accounts.dev instead of here.
  signInUrl: '/login',
  signUpUrl: '/registration',
  signInFallbackRedirectUrl: '/',
  signUpFallbackRedirectUrl: '/',
}) //?

app.mount('#app')
sessionStorage.removeItem(VITE_PRELOAD_RELOAD_KEY)
